/* eslint-disable import/no-internal-modules */
import { Button, Root } from '@studio/design-system/button';
import type { Api as DialogApi, Props as DialogProps } from '@studio/design-system/dialog';
import { Input } from '@studio/design-system/input';
import { Message, Prompt } from '@studio/design-system/prompt';
import T from '@studio/design-system/themes';
import { useLatestFn } from '@studio/react-utils/hooks';
import { forwardRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Success } from '#~/routes/agent/link/success.jsx';

const OrganizationIcon = (props: { className: string }): ReactElement => (
  <Root className={props.className} tabIndex={-1}>
    <svg
      fill='none'
      height='106'
      viewBox='0 0 124 106'
      width='124'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M62 41.3541C73.6182 41.3541 83.0702 32.0784 83.0702 20.6771C83.0702 9.27568 73.6182 0 62 0C50.3818 0 40.9297 9.27568 40.9297 20.6771C40.9297 32.0784 50.3818 41.3541 62 41.3541ZM62 7.13002C69.6119 7.13002 75.8046 13.2072 75.8046 20.6771C75.8046 28.1469 69.6119 34.2241 62 34.2241C54.388 34.2241 48.1953 28.1469 48.1953 20.6771C48.1953 13.2072 54.388 7.13002 62 7.13002Z'
        fill='#3664FF'
      />
      <path
        d='M104.625 41.3563C111.97 41.3563 117.945 35.4923 117.945 28.2846C117.945 21.0769 111.97 15.2129 104.625 15.2129C97.2802 15.2129 91.3047 21.0769 91.3047 28.2846C91.3047 35.4923 97.2802 41.3563 104.625 41.3563ZM104.625 22.3429C107.964 22.3429 110.68 25.0083 110.68 28.2846C110.68 31.5608 107.964 34.2263 104.625 34.2263C101.286 34.2263 98.5703 31.5608 98.5703 28.2846C98.5703 25.0083 101.286 22.3429 104.625 22.3429Z'
        fill='#3664FF'
      />
      <path
        d='M107.555 49.4355H101.937C95.3154 49.4355 89.5998 53.2743 86.9953 58.7882C81.633 53.0438 73.9307 49.4355 65.3854 49.4355H58.6146C50.0693 49.4355 42.367 53.0438 37.0047 58.7882C34.4 53.2743 28.6844 49.4355 22.0632 49.4355H16.4445C7.37702 49.4355 0 56.6312 0 65.4757V91.4314C0 95.2711 3.19542 98.395 7.12321 98.395H29.3383C29.7108 102.649 33.3562 106 37.7865 106H86.2135C90.6436 106 94.289 102.649 94.6617 98.395H116.623C120.691 98.395 124 95.1604 124 91.1842V65.4757C124 56.6312 116.623 49.4355 107.555 49.4355ZM7.26561 65.4757C7.26561 60.5627 11.3833 56.5656 16.4445 56.5656H22.0632C27.1245 56.5656 31.2421 60.5627 31.2421 65.4757V67.9168C28.7992 74.1589 29.3046 77.8998 29.3046 91.265H7.26561V65.4757ZM87.4295 97.677C87.4295 98.3351 86.8841 98.8703 86.2135 98.8703H37.7863C37.1156 98.8703 36.5702 98.3349 36.5702 97.677V78.1985C36.5702 66.27 46.4592 56.5656 58.6143 56.5656H65.3852C77.5405 56.5656 87.4295 66.27 87.4295 78.1983V97.677ZM116.734 91.1842C116.734 91.3071 118.088 91.265 94.6951 91.265C94.6951 77.8009 95.1967 74.1487 92.7576 67.9168V65.4757C92.7576 60.5627 96.8753 56.5656 101.937 56.5656H107.555C112.616 56.5656 116.734 60.5627 116.734 65.4757V91.1842Z'
        fill='#3664FF'
      />
      <path
        d='M19.375 41.3563C26.7198 41.3563 32.6953 35.4923 32.6953 28.2846C32.6953 21.0769 26.7198 15.2129 19.375 15.2129C12.0302 15.2129 6.05469 21.0769 6.05469 28.2846C6.05469 35.4923 12.0302 41.3563 19.375 41.3563ZM19.375 22.3429C22.7135 22.3429 25.4297 25.0083 25.4297 28.2846C25.4297 31.5608 22.7135 34.2263 19.375 34.2263C16.0364 34.2263 13.3203 31.5608 13.3203 28.2846C13.3203 25.0083 16.0364 22.3429 19.375 22.3429Z'
        fill='#3664FF'
      />
    </svg>
  </Root>
);

/** @internal */
export type CreateOrganizationDialogProps = DialogProps & {
  /** Called when the dialog is confirmed. */
  onConfirm: (
    name: string,
    onSuccess: (id: Uint8Array, onCreateStudio: (id: Uint8Array) => void) => void,
    onError: () => void
  ) => void;

  /** Called when the dialog is dismissed. */
  onDismiss: () => void;
};

/** @internal */
export const CreateOrganizationDialog = forwardRef<DialogApi, CreateOrganizationDialogProps>(
  ({ onConfirm, onDismiss }, forwardedRef): ReactElement => {
    const [orgName, setOrgName] = useState<string>();
    const [isBusy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [organizationId, setOrganizationId] = useState<Uint8Array>();

    const [createStudioCallback, setCreateStudioCallback] = useState<(id: Uint8Array) => void>();

    const handleSuccess = useLatestFn(
      (id: Uint8Array, onCreateStudio: (id: Uint8Array) => void) => {
        setBusy(false);
        setOrganizationId(id);

        setCreateStudioCallback(() => onCreateStudio);
      }
    );

    const handleError = useLatestFn(() => {
      setBusy(false);
      setError('Failed to create organization. Please try again.');
    });

    const handleSave = useLatestFn(() => {
      if (!orgName || orgName.length === 0) {
        setError('Organization name is required.');

        return;
      }

      setError(undefined);
      setBusy(true);

      onConfirm(orgName, handleSuccess, handleError);
    });

    const handleChange = useLatestFn((name: string) => {
      setOrgName(name);
      setError(undefined);
    });

    const handleCreateStudio = useLatestFn(() => {
      onDismiss();

      setTimeout(() => {
        createStudioCallback?.(organizationId!);
      }, 250);
    });

    const content =
      organizationId !== undefined ? (
        <>
          <div className='mb-32 mt-16 w-full overflow-hidden'>
            <Success className='mx-auto' />
          </div>

          <h1>{orgName} created</h1>
          <p className='leading-150'>
            Your new organization has been created. Next create a new studio to begin using the
            organization.
          </p>

          <div>
            <Button
              className='mx-auto mt-32 px-64'
              data-theme={T.getTheme('light')}
              onClick={handleCreateStudio}
              size='xl'
              type='button'
            >
              Create Studio
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className='mb-32 mt-16 w-full overflow-hidden'>
            <OrganizationIcon className='mb-16' />
            <div className='mb-32 text-lg font-demibold'>
              {(orgName?.length ?? 0) > 0 ? orgName : 'My Organization'}
            </div>

            <hr />
          </div>

          <h1>Create an Organization</h1>
          <p className='leading-150'>
            Your Organization allows you to organize and manage your Studios. You can invite other
            users to your Organization to give them access.
          </p>
          <div className='mt-32'>
            {!!error && <p className='text-error'>{error}</p>}
            <Input
              className='mt-4'
              data-theme={T.getTheme('light')}
              onChange={handleChange}
              placeholder='Organization Name'
              size='xl'
              type='text'
              value={orgName}
            />
          </div>

          <div>
            <Button
              className='mx-auto mt-32 px-64'
              data-theme={T.getTheme('light')}
              disabled={isBusy || !orgName}
              onClick={handleSave}
              size='xl'
              type='button'
            >
              {isBusy ? 'Creating Organization...' : 'Create Organization'}
            </Button>
          </div>
        </>
      );

    return (
      <div>
        <Prompt className='w-[425px]' message={<Message>{content}</Message>} ref={forwardedRef} />
      </div>
    );
  }
);
